import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Percent, TrendingUp, UserCheck, Users, UserX } from "lucide-react";
import { useState } from "react";
import { Cell, Legend, Pie, PieChart } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import type { StatsPeriod } from "../api/newsletterApi";
import {
  useDeleteSubscription,
  useNewsletterStats,
  useNewsletterSubscriptions,
} from "../hooks/useNewsletter";
import AddSubscriptionModal from "./AddSubscriptionModal";

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  iconColor?: string;
}

const StatsCard = ({
  title,
  value,
  icon,
  description,
  iconColor = "text-purple-400",
}: StatsCardProps) => (
  <Card className="border-neutral-800 bg-neutral-900/50">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-neutral-500">{title}</p>
          <p className="mt-1 text-2xl font-bold text-white">
            {typeof value === "number" ? value.toLocaleString("es-AR") : value}
          </p>
          {description && <p className="mt-0.5 text-xs text-neutral-400">{description}</p>}
        </div>
        <div className={`rounded-lg bg-neutral-800/50 p-2.5 ${iconColor}`}>{icon}</div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({ status }: { status: "active" | "unsubscribed" }) => {
  const isActive = status === "active";
  return (
    <Badge className={isActive ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
      {isActive ? "Activo" : "Desuscrito"}
    </Badge>
  );
};

const SourceBadge = ({ source }: { source: string }) => {
  const labels: Record<string, string> = {
    public_form: "Formulario público",
    signup: "Registro (consentimiento)",
    manual_admin: "Manual (Admin)",
    import: "Importación",
  };
  return (
    <Badge variant="outline" className="border-neutral-600 text-neutral-300">
      {labels[source] || source}
    </Badge>
  );
};

const chartConfig = {
  active: {
    label: "Activos",
    color: "hsl(142, 76%, 36%)",
  },
  unsubscribed: {
    label: "Desuscritos",
    color: "hsl(0, 84%, 60%)",
  },
} satisfies ChartConfig;

const NewsletterPage = () => {
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "unsubscribed" | "deleted">(
    "all"
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [chartPeriod, setChartPeriod] = useState<StatsPeriod>("all");

  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } = useNewsletterSubscriptions(
    {
      page: currentPage,
      limit: 20,
      status: statusFilter,
      search: searchTerm,
    }
  );

  const { data: stats } = useNewsletterStats(chartPeriod);
  const { mutate: deleteSubscription } = useDeleteSubscription();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filter: "all" | "active" | "unsubscribed" | "deleted") => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  const handleDelete = (id: string) => {
    if (window.confirm("¿Estás seguro de que deseas eliminar este suscriptor?")) {
      deleteSubscription(id);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy HH:mm", { locale: es });
  };

  const subscriptions = subscriptionsData?.data || [];
  const totalPages = subscriptionsData?.totalPages || 1;
  const hasSubscriptions = subscriptions.length > 0;

  const filterLabels = {
    all: "Todos",
    active: "Activos",
    unsubscribed: "Desuscritos",
    deleted: "Eliminados",
  };

  const periodLabels = { day: "Hoy", week: "Semana", month: "Mes", all: "Todo" };

  const pieData = stats
    ? [
        { name: "active", value: stats.active, fill: "var(--color-active)" },
        { name: "unsubscribed", value: stats.unsubscribed, fill: "var(--color-unsubscribed)" },
      ]
    : [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Newsletter"
        subtitle="Gestión de suscriptores de newsletter"
        actions={
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            Agregar suscriptor
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatsCard
            title="Total"
            value={stats.total}
            icon={<Users className="h-5 w-5" />}
            iconColor="text-purple-400"
          />
          <StatsCard
            title="Activos"
            value={stats.active}
            icon={<UserCheck className="h-5 w-5" />}
            iconColor="text-green-400"
          />
          <StatsCard
            title="Desuscritos"
            value={stats.unsubscribed}
            icon={<UserX className="h-5 w-5" />}
            iconColor="text-red-400"
          />
          <StatsCard
            title="Nuevos esta semana"
            value={stats.newThisWeek}
            icon={<TrendingUp className="h-5 w-5" />}
            iconColor="text-blue-400"
          />
          <StatsCard
            title="Tasa de retención"
            value={`${stats.retentionRate.toFixed(2)}%`}
            icon={<Percent className="h-5 w-5" />}
            iconColor="text-amber-400"
          />
        </div>
      )}

      <div className="flex gap-6">
        {/* Table - Left Side */}
        <Card className="flex-1 border-neutral-800 bg-neutral-900/50">
          <CardContent className="space-y-4 p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex flex-wrap gap-2">
                {(["all", "active", "unsubscribed", "deleted"] as const).map((filter) => (
                  <Button
                    key={filter}
                    variant={statusFilter === filter ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleFilterChange(filter)}
                    className={
                      statusFilter === filter
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                    }
                  >
                    {filterLabels[filter]}
                  </Button>
                ))}
              </div>
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Buscar por email..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
                />
              </div>
            </div>

            {isLoadingSubscriptions ? (
              <div className="py-12">
                <LoadingOverlay />
              </div>
            ) : !hasSubscriptions ? (
              <EmptyState
                title="No hay suscriptores"
                description={
                  searchTerm
                    ? "No se encontraron suscriptores con ese criterio de búsqueda."
                    : "Aún no hay suscriptores en el newsletter."
                }
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-800 hover:bg-transparent">
                      <TableHead className="text-neutral-400">Email</TableHead>
                      <TableHead className="text-neutral-400">Estado</TableHead>
                      <TableHead className="text-neutral-400">Fuente</TableHead>
                      <TableHead className="text-neutral-400">Suscrito</TableHead>
                      <TableHead className="text-neutral-400">Desuscrito</TableHead>
                      <TableHead className="text-right text-neutral-400">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {subscriptions.map((subscription) => (
                      <TableRow
                        key={subscription.id}
                        className="border-neutral-800 hover:bg-neutral-800/50"
                      >
                        <TableCell className="text-neutral-200">{subscription.email}</TableCell>
                        <TableCell>
                          <StatusBadge status={subscription.status} />
                        </TableCell>
                        <TableCell>
                          <SourceBadge source={subscription.source} />
                        </TableCell>
                        <TableCell className="text-neutral-400">
                          {formatDate(subscription.subscribedAt)}
                        </TableCell>
                        <TableCell className="text-neutral-400">
                          {subscription.unsubscribedAt
                            ? formatDate(subscription.unsubscribedAt)
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(subscription.id)}
                            className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          >
                            Eliminar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                    >
                      Anterior
                    </Button>
                    <span className="text-sm text-neutral-400">
                      Página {currentPage} de {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Pie Chart - Right Side */}
        <Card className="w-80 flex-shrink-0 border-neutral-800 bg-neutral-900/50">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap justify-center gap-2">
              {(["day", "week", "month", "all"] as const).map((period) => (
                <Button
                  key={period}
                  variant={chartPeriod === period ? "default" : "outline"}
                  size="sm"
                  onClick={() => setChartPeriod(period)}
                  className={
                    chartPeriod === period
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  }
                >
                  {periodLabels[period]}
                </Button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <CardTitle className="mb-4 text-center text-sm font-medium text-neutral-400">
              Distribución
            </CardTitle>
            {stats && stats.total > 0 ? (
              <ChartContainer config={chartConfig} className="mx-auto aspect-square h-[220px]">
                <PieChart>
                  <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    strokeWidth={2}
                    stroke="hsl(var(--background))"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Legend
                    verticalAlign="bottom"
                    height={36}
                    formatter={(value) => (
                      <span className="text-xs text-neutral-300">
                        {value === "active" ? "Activos" : "Desuscritos"}
                      </span>
                    )}
                  />
                </PieChart>
              </ChartContainer>
            ) : (
              <div className="flex h-[220px] items-center justify-center text-sm text-neutral-400">
                Sin datos para este período
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isModalOpen && <AddSubscriptionModal onClose={() => setIsModalOpen(false)} />}
    </div>
  );
};

export default NewsletterPage;
